// The list of items in a drop down selection menu.
// @flow
import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import TextField from 'material-ui/TextField';
import Paper from 'material-ui/Paper';
import SelectableItem from './SelectableItem';

import type { Suggestion } from './constants';

type Props = {
    onSelect: number => void,
    onCancel: () => void,
    options: Array<Suggestion>
};

type State = {
    searchTerm: text,
    suggestions: Array<Suggestion>
};

type InputProps = {
    placeholder: text,
    value: text,
    onChange: (Event, Search) => void,
    onBlur: () => void
};

type Query = {
    // eslint-disable-next-line
    value: text
};

type Search = {
    newValue: text
};

type QueryConfiguration = {
    query: text,
    isHighlighted: boolean
};

const DEFAULT_SEARCH_PLACEHOLDER_TEXT = 'Search...';

export default class DropDownSelector extends Component<Props, State> {
    state: State = {
        searchTerm: '',
        suggestions: []
    };

    renderInput = (inputProps: InputProps) => {
        const { ref, ...other } = inputProps;
        return (
            <TextField
                autoFocus
                fullWidth
                InputProps={{
                    inputRef: ref,
                    ...other
                }}
            />
        );
    };

    // Suggestion API

    renderSuggestion = (
        { image, text }: Suggestion,
        { query, isHighlighted }: QueryConfiguration
    ) => (
        <SelectableItem
            image={image}
            text={text}
            query={query}
            selected={isHighlighted}
        />
    );

    // Generates suggestions based off of the search term to filter on.
    getSuggestions = (filterValue: text) => {
        const { options } = this.props;

        // Return the original dataset if there's nothing
        // to filter on.
        if (!filterValue) {
            return options;
        }

        const normalizedFilterValue = filterValue.trim().toLowerCase();

        return options.filter((suggestion: Suggestion) => {
            let regex = new RegExp(`${normalizedFilterValue}+`, 'gi');
            return regex.test(suggestion.text);
        });
    };

    // Sets the suggestions that should be displayed on the state.
    handleSuggestionsFetchRequested = ({ value }: Query) => {
        this.setState({
            suggestions: this.getSuggestions(value)
        });
    };

    // Clears suggestions when cleared.
    handleSuggestionsClearRequested = () => {
        this.setState({
            suggestions: []
        });
    };

    renderSuggestionsContainer = (options: any) => {
        const { containerProps, children } = options;

        return (
            <Paper {...containerProps} square>
                {children}
            </Paper>
        );
    };

    // Updates the search term value.
    handleChange = (event: Event, { newValue }: Search) => {
        this.setState({
            searchTerm: newValue
        });
    };

    // End Suggestion API

    render() {
        return (
            <Autosuggest
                renderInputComponent={this.renderInput}
                suggestions={this.state.suggestions}
                onSuggestionSelected={this.props.onSelect}
                onSuggestionsFetchRequested={
                    this.handleSuggestionsFetchRequested
                }
                onSuggestionsClearRequested={
                    this.handleSuggestionsClearRequested
                }
                renderSuggestionsContainer={this.renderSuggestionsContainer}
                getSuggestionValue={(s: Suggestion) => s.text}
                highlightFirstSuggestion={true}
                shouldRenderSuggestions={() => true}
                renderSuggestion={this.renderSuggestion}
                inputProps={{
                    placeholder:
                        this.props.placeholder ||
                        DEFAULT_SEARCH_PLACEHOLDER_TEXT,
                    value: this.state.searchTerm,
                    onChange: this.handleChange,
                    onBlur: this.props.onCancel
                }}
            />
        );
    }
}
